'use client';

import React, { useState } from 'react';
import { CheckCircle2, ArrowRight, X, Eye, EyeOff, Loader2, MapPin, Wrench } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface FormState {
  name: string;
  email: string;
  password: string;
  postcode: string;
}

const PERKS = [
  'Post jobs for free — no hidden fees',
  'Get matched with verified local tradespeople',
  'Read real reviews from your neighbours',
  'Message providers directly',
];

export default function HomeownerSignup() {
  const [form, setForm] = useState<FormState>({ name: '', email: '', password: '', postcode: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const supabase = createClient();
      const { error: signUpError } = await supabase.auth.signUp({
        email: form.email,
        password: form.password,
        options: {
          data: {
            full_name: form.name,
            postcode: form.postcode,
            account_type: 'homeowner',
          },
          emailRedirectTo: `${window.location.origin}/homepage`,
        },
      });
      if (signUpError) throw signUpError;
      setSuccess(true);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-16 md:py-20" id="homeowner-signup" style={{ backgroundColor: '#F8F5FA' }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section header */}
        <div className="text-center mb-10">
          <span
            className="inline-block text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full mb-3"
            style={{ backgroundColor: '#3A204718', color: '#3A2047' }}
          >
            For Homeowners
          </span>
          <h2 className="font-display text-2xl md:text-3xl font-bold mb-3" style={{ color: '#250C31' }}>
            Post your first job in minutes
          </h2>
          <p className="text-gray-500 max-w-md mx-auto">
            Create a free account and start connecting with trusted tradespeople in your area today.
          </p>
        </div>

        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8 items-start">
          {/* Left — perks */}
          <div className="flex flex-col gap-6">
            <div
              className="rounded-2xl p-6"
              style={{ background: 'linear-gradient(135deg, #250C31 0%, #3A2047 100%)' }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-love-mint/20 flex items-center justify-center">
                  <Wrench size={20} className="text-love-mint" />
                </div>
                <div>
                  <p className="text-white font-semibold">Love Locals for Homeowners</p>
                  <p className="text-white/60 text-xs">Free forever</p>
                </div>
              </div>
              <ul className="space-y-3">
                {PERKS.map((perk, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-white/80">
                    <CheckCircle2 size={16} className="text-love-mint shrink-0 mt-0.5" />
                    {perk}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl bg-white border border-gray-100 shadow-card p-5">
              <div className="flex items-center gap-2 mb-3">
                <MapPin size={16} style={{ color: '#F05A28' }} />
                <p className="text-sm font-semibold text-gray-800">How it works</p>
              </div>
              <ol className="space-y-2">
                {[
                  'Create your free account below',
                  'Describe the job and your location',
                  'Get matched with verified local pros',
                  'Choose, book, and leave a review',
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-3 text-xs text-gray-600">
                    <span
                      className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5"
                      style={{ backgroundColor: '#3A204718', color: '#3A2047' }}
                    >
                      {i + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Right — signup form */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-6 md:p-8">
            {success ? (
              <div className="flex flex-col items-center text-center py-6 gap-4">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: '#99E6C818' }}
                >
                  <CheckCircle2 size={28} style={{ color: '#059669' }} />
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900 font-display">Account created!</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Check your email to confirm your account, then you can start posting jobs.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <h3 className="font-display font-bold text-gray-900 text-lg mb-5">Create your free account</h3>

                {error && (
                  <div className="flex items-start gap-2 p-3 rounded-xl mb-4 text-sm" style={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}>
                    <X size={15} className="shrink-0 mt-0.5" />
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5" htmlFor="signup-name">
                      Full name <span className="text-red-400">*</span>
                    </label>
                    <input
                      id="signup-name"
                      name="name"
                      type="text"
                      autoComplete="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Jane Smith"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-love-plum-light transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5" htmlFor="signup-email">
                      Email address <span className="text-red-400">*</span>
                    </label>
                    <input
                      id="signup-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="jane@example.com"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-love-plum-light transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5" htmlFor="signup-password">
                      Password <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <input
                        id="signup-password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        value={form.password}
                        onChange={handleChange}
                        placeholder="Min. 6 characters"
                        className="w-full px-4 py-2.5 pr-10 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-love-plum-light transition-colors"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5" htmlFor="signup-postcode">
                      Postcode <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <input
                      id="signup-postcode"
                      name="postcode"
                      type="text"
                      autoComplete="postal-code"
                      value={form.postcode}
                      onChange={handleChange}
                      placeholder="e.g. LS1 4AP"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-love-plum-light transition-colors"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all duration-200 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                    style={{ backgroundColor: '#3A2047', color: '#fff' }}
                  >
                    {loading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Creating account…
                      </>
                    ) : (
                      <>
                        Create Account & Post a Job
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>

                  <p className="text-xs text-gray-400 text-center mt-2">
                    Already have an account?{' '}
                    <button type="button" className="text-love-plum-light font-medium hover:underline">
                      Log in
                    </button>
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
